import React from 'react';
import { PiDownloadSimple } from 'react-icons/pi';
import { MediaSkeleton } from '../../atoms/media/media-skeleton';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Typography } from '@material-tailwind/react';
import { PdfMedia } from './pdf-media';
import { ImageMedia } from './image-media';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

interface FileMediaProps {
  url: string;
  fileName: string;
  isLoading?: boolean;
  className?: string;
  footer?: React.ReactNode;
}

export const FileMedia: React.FC<FileMediaProps> = ({
  url,
  fileName,
  isLoading = false,
  className = '',
  footer = null,
}) => {
  const containerClasses = `flex relative w-full h-auto max-w-[27rem] max-h-[27rem] rounded-lg ${className}`;
  const fileUrl = isLoading ? undefined : url;

  const getExtension = (nameOrUrl: string): string => {
    const name = nameOrUrl.split('?')[0].split('#')[0];
    const lastDot = name.lastIndexOf('.');
    if (lastDot > -1 && lastDot < name.length - 1) {
      return name.substring(lastDot + 1).toLowerCase();
    }
    return '';
  };

  const handleDownload = () => {
    if (!fileUrl) return;

    window.open(fileUrl, '_blank');
  };

  const derivedName =
    fileName && fileName.trim().length > 0 ? fileName.trim() : 'document';
  const extensionFromName = getExtension(derivedName);
  const extensionFromUrl = getExtension(url);
  const extension = (extensionFromName || extensionFromUrl).toLowerCase();
  const isPdf = extension === 'pdf';
  const isImageLike = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'svg',
    'bmp',
    'tif',
    'tiff',
    'avif',
  ].includes(extension);

  if (isPdf) {
    return (
      <PdfMedia
        url={url}
        fileName={derivedName}
        isLoading={isLoading}
        className={className}
        footer={footer}
      />
    );
  }

  if (isImageLike) {
    return (
      <ImageMedia
        url={url}
        isLoading={isLoading}
        alt={derivedName}
        className={className}
        footer={footer}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col w-full gap-2 justify-start">
        <div className={containerClasses}>
          {isLoading && <MediaSkeleton type="file" />}

          {/* TODO: check when implementing i18n */}
          <Tooltip content="Descargar archivo" placement="top" size="sm">
            <Button
              variant="secondary"
              appearance="soft"
              size="lg"
              onClick={handleDownload}
              className={`!h-14 w-64 max-w-full justify-between items-center ${
                !isLoading ? 'flex' : 'hidden'
              }`}
            >
              <Typography className="flex flex-1 min-w-0 text-sm font-normal text-primary-600 text-left whitespace-normal break-words line-clamp-2">
                {derivedName}
              </Typography>

              <PiDownloadSimple className="w-5 h-5 text-primary-600" />
            </Button>
          </Tooltip>
        </div>

        {footer && footer}
      </div>
    </>
  );
};
